"use client";

import React, { useState, useEffect } from "react";
import { VariantCombination, PaymentType } from "./VariantTable";
import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/StandardButton";
import { Switch } from "@/components/ui/switch";
import { X, CheckCircle2 } from "lucide-react";
import QuillEditor from "./QuillEditor";
import ImageUpload from "@/components/ImageUpload/ImageUpload";

const PAYMENT_OPTIONS: { key: PaymentType; title: string; description: string; icon: string }[] = [
  {
    key: "PREPAID",
    title: "Prepaid",
    description: "Customer pays before order processing",
    icon: "💳",
  },
  {
    key: "POSTPAID",
    title: "Postpaid (COD)",
    description: "Customer pays after delivery",
    icon: "💵",
  },
  {
    key: "SEMIPREPAID",
    title: "Semi-Prepaid",
    description: "Partial payment upfront, rest on delivery",
    icon: "💰",
  },
];

type Props = {
  variant: VariantCombination | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (variant: VariantCombination) => void;
  onApplyToAll?: (variant: VariantCombination) => void;
};

export default function VariantDetailModal({
  variant,
  isOpen,
  onClose,
  onSave,
  onApplyToAll,
}: Props) {
  const [editedVariant, setEditedVariant] = useState<VariantCombination | null>(
    null,
  );
  const [variantImages, setVariantImages] = useState<string[]>([]);
  const [variantAssets, setVariantAssets] = useState<Array<{ asset_id: string; asset?: { path: string; original_name?: string; [key: string]: any } }>>([]);

  useEffect(() => {
    if (variant) {
      setEditedVariant({ ...variant });
      // Use variantAssets for display if available
      if (variant.variantAssets && variant.variantAssets.length > 0) {
        setVariantAssets(variant.variantAssets);
        // Extract asset paths for ImageUpload component
        setVariantImages(variant.variantAssets.map(va => va.asset?.path || '').filter(Boolean));
      } else {
        // Fall back to images array or legacy image field
        setVariantImages(variant.images || (variant.image ? [variant.image] : []));
        setVariantAssets([]);
      }
    }
  }, [variant]);

  if (!isOpen || !editedVariant) return null;

  const handleSave = () => {
    if (editedVariant) {
      const updatedVariant = {
        ...editedVariant,
        variantAssets: variantAssets,
        images: variantAssets.map(va => va.asset_id),
        image: variantAssets[0]?.asset_id || undefined, // Keep legacy field for backward compatibility
      };
      onSave(updatedVariant);
      onClose();
    }
  };

  const handleApplyToAll = () => {
    if (editedVariant && onApplyToAll) {
      const confirmed = window.confirm(
        "This will apply the current variant's pricing, inventory, and shipping settings to ALL other variants. This action cannot be undone. Are you sure you want to continue?",
      );

      if (confirmed) {
        const updatedVariant = {
          ...editedVariant,
          variantAssets: variantAssets,
          images: variantAssets.map(va => va.asset_id),
          image: variantAssets[0]?.asset_id || undefined, // Keep legacy field for backward compatibility
        };
        onApplyToAll(updatedVariant);
        onClose();
      }
    }
  };

  const handleImageChange = (file: File | null, id?: number) => {
    if (id !== undefined) {
      setVariantImages((prev) => prev.filter((_, i) => i !== id));
      return;
    }
    if (!file) return;
    // For now, just use a placeholder - in real implementation this would upload
    const placeholderUrl = URL.createObjectURL(file);
    setVariantImages([placeholderUrl]);
  };

  const updateVariant = (field: keyof VariantCombination, value: any) => {
    if (!editedVariant) return;
    setEditedVariant({ ...editedVariant, [field]: value });
  };

  const updateShippingDetail = (
    field: keyof VariantCombination["shippingDetails"],
    value: any,
  ) => {
    if (!editedVariant) return;
    setEditedVariant({
      ...editedVariant,
      shippingDetails: { ...editedVariant.shippingDetails, [field]: value },
    });
  };

  const togglePaymentType = (paymentType: PaymentType) => {
    if (!editedVariant) return;
    const currentTypes = editedVariant.supported_payment_types || [];
    const isSelected = currentTypes.includes(paymentType);

    let newTypes: PaymentType[];
    if (isSelected) {
      newTypes = currentTypes.filter((t) => t !== paymentType);
      // If removing SEMIPREPAID, also clear the semi_prepaid_amount
      if (paymentType === "SEMIPREPAID") {
        setEditedVariant({
          ...editedVariant,
          supported_payment_types: newTypes,
          semi_prepaid_amount: undefined,
        });
        return;
      }
    } else {
      newTypes = [...currentTypes, paymentType];
    }

    setEditedVariant({
      ...editedVariant,
      supported_payment_types: newTypes,
    });
  };

  const isPaymentTypeSelected = (paymentType: PaymentType): boolean => {
    return editedVariant?.supported_payment_types?.includes(paymentType) || false;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-background rounded-t-lg z-10 flex items-center justify-between p-6 shadow-md dark:shadow-[0_4px_6px_-1px_rgb(0_0_0)]">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Variant Details
            </h2>
            <p className="text-sm text-muted-foreground">
              {editedVariant.optionString}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Information Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Basic Information
            </h3>
            <StandardFormInput
              label="Variant Title"
              type="text"
              placeholder="Custom title for this variant"
              value={editedVariant.title}
              onChange={(e) => updateVariant("title", e.target.value)}
            />
            <div className="space-y-1">
              <label className="block text-[13px] font-normal text-muted-foreground">
                Variant Image
              </label>
              <ImageUpload
                images={variantImages}
                onChange={handleImageChange}
                type="single"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[13px] font-normal text-muted-foreground">
                Variant Description
              </label>
              <QuillEditor
                initialValue={editedVariant.description}
                onChange={(value) => updateVariant("description", value)}
                placeholder="Write variant description..."
                height={100}
              />
            </div>
          </div>

          {/* Pricing & SKU Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Pricing & Identification
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <StandardFormInput
                label="Price ($)"
                type="number"
                step={0.01}
                min={0}
                placeholder="0.00"
                value={editedVariant.price / 100}
                onChange={(e) =>
                  updateVariant("price", Number(e.target.value) * 100)
                }
              />
              <StandardFormInput
                label="Listing Price ($)"
                type="number"
                step={0.01}
                min={0}
                placeholder="0.00"
                value={editedVariant.listingPrice / 100}
                onChange={(e) =>
                  updateVariant("listingPrice", Number(e.target.value) * 100)
                }
              />
              <StandardFormInput
                label="SKU"
                type="text"
                placeholder="SKU"
                value={editedVariant.sku}
                onChange={(e) => updateVariant("sku", e.target.value)}
              />
              <StandardFormInput
                label="Barcode"
                type="text"
                placeholder="Barcode"
                value={editedVariant.barcode || ""}
                onChange={(e) => updateVariant("barcode", e.target.value)}
              />
            </div>
          </div>

          {/* Inventory Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Inventory Management
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-[13px] font-normal text-muted-foreground">
                  Track Inventory
                </label>
                <div className="flex items-center justify-between bg-background rounded-lg px-4 py-2 h-[38px]">
                  <span className="text-sm text-foreground">
                    Enable inventory tracking
                  </span>
                  <Switch
                    checked={editedVariant.trackInventory}
                    onCheckedChange={(checked) =>
                      updateVariant("trackInventory", checked)
                    }
                  />
                </div>
              </div>
              <StandardFormInput
                label="Inventory Quantity"
                type="number"
                min={0}
                placeholder="0"
                disabled={!editedVariant.trackInventory}
                value={editedVariant.inventory}
                onChange={(e) =>
                  updateVariant("inventory", Number(e.target.value))
                }
              />
            </div>
          </div>

          {/* Shipping Details Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Shipping Details
            </h3>

            {/* Weight and Unit */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StandardFormInput
                label="Weight"
                type="number"
                min={0}
                placeholder="0"
                value={editedVariant.shippingDetails.weight}
                onChange={(e) =>
                  updateShippingDetail("weight", Number(e.target.value))
                }
              />
              <StandardFormSelect
                label="Weight Unit"
                value={editedVariant.shippingDetails.weightUnit}
                onValueChange={(value) =>
                  updateShippingDetail("weightUnit", value)
                }
                options={[
                  { label: "Grams (g)", value: "g" },
                  { label: "Kilograms (kg)", value: "kg" },
                  { label: "Pounds (lb)", value: "lb" },
                  { label: "Ounces (oz)", value: "oz" },
                ]}
              />
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <StandardFormInput
                label="Width (mm)"
                type="number"
                min={0}
                placeholder="0"
                value={editedVariant.shippingDetails.width}
                onChange={(e) =>
                  updateShippingDetail("width", Number(e.target.value))
                }
              />
              <StandardFormInput
                label="Height (mm)"
                type="number"
                min={0}
                placeholder="0"
                value={editedVariant.shippingDetails.height}
                onChange={(e) =>
                  updateShippingDetail("height", Number(e.target.value))
                }
              />
              <StandardFormInput
                label="Length (mm)"
                type="number"
                min={0}
                placeholder="0"
                value={editedVariant.shippingDetails.length}
                onChange={(e) =>
                  updateShippingDetail("length", Number(e.target.value))
                }
              />
            </div>

            {/* Shipping Class and Price */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StandardFormInput
                label="Shipping Class"
                type="text"
                placeholder="Standard, Express, etc."
                value={editedVariant.shippingDetails.shippingClass}
                onChange={(e) =>
                  updateShippingDetail("shippingClass", e.target.value)
                }
              />
              <StandardFormInput
                label="Shipping Price ($)"
                type="number"
                min={0}
                step={0.01}
                placeholder="0.00"
                value={editedVariant.shippingDetails.shippingPrice / 100}
                onChange={(e) =>
                  updateShippingDetail(
                    "shippingPrice",
                    Number(e.target.value) * 100,
                  )
                }
              />
            </div>

            {/* Free Shipping Toggle */}
            <div className="flex items-center justify-between bg-background rounded-lg px-4 py-2 h-[38px]">
              <span className="text-sm text-foreground">Free Shipping</span>
              <Switch
                checked={editedVariant.shippingDetails.freeShipping}
                onCheckedChange={(checked) =>
                  updateShippingDetail("freeShipping", checked)
                }
              />
            </div>
          </div>

          {/* Supported Payment Types Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Supported Payment Types
            </h3>
            <p className="text-xs text-muted-foreground">
              Select which payment methods are available for this variant
            </p>

            <div className="flex flex-wrap gap-3">
              {PAYMENT_OPTIONS.map((option) => {
                const isSelected = isPaymentTypeSelected(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => togglePaymentType(option.key)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent shadow-md"
                        : "border-border bg-background hover:border-primary/40 hover:shadow-md"
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 shadow-md">
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        </div>
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                      )}
                    </div>
                    <div className="text-xl">{option.icon}</div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-foreground">
                        {option.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {option.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Semi-Prepaid Amount Input */}
            {isPaymentTypeSelected("SEMIPREPAID") && (
              <div className="mt-4 p-4 bg-background rounded-lg border border-border">
                <StandardFormInput
                  label="Semi-Prepaid Amount (in Rupees)"
                  type="number"
                  min={0}
                  max={editedVariant.price / 100}
                  step={1}
                  placeholder="Enter amount (e.g., 200)"
                  value={
                    editedVariant.semi_prepaid_amount
                      ? editedVariant.semi_prepaid_amount / 100
                      : ""
                  }
                  onChange={(e) => {
                    const valueInRupees = Number(e.target.value);
                    const maxValueInRupees = editedVariant.price / 100;
                    // Ensure the value doesn't exceed the variant price
                    const clampedValue = Math.min(valueInRupees, maxValueInRupees);
                    const valueInPaise = clampedValue * 100;
                    updateVariant("semi_prepaid_amount", valueInPaise || undefined);
                  }}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  This is the partial payment amount customers will pay upfront (max: {(editedVariant.price / 100).toFixed(2)})
                </p>
                {editedVariant.semi_prepaid_amount && editedVariant.semi_prepaid_amount > editedVariant.price && (
                  <p className="text-xs text-destructive mt-1">
                    Amount cannot exceed the variant price
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Metafields Card */}
          <div className="bg-secondary-background border border-background rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">Metafields</h3>
            <div className="p-4 bg-background rounded-lg">
              <p className="text-sm text-muted-foreground">
                Metafields configuration will be available once the settings
                page is implemented.
              </p>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="sticky bottom-0 bg-background rounded-b-lg z-10 flex items-center justify-end gap-3 p-6 shadow-[0_-6px_10px_-2px_rgb(0_0_0_/_0.15)] dark:shadow-[0_-6px_10px_-2px_rgb(0_0_0)]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-secondary-background rounded hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          {onApplyToAll && (
            <ButtonOutline
              type="button"
              onClick={handleApplyToAll}
              shape="rounded"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              title="Apply this variant's pricing, inventory, and shipping settings to all other variants"
            >
              Apply to All
            </ButtonOutline>
          )}
          <ButtonPrimary type="button" onClick={handleSave}>
            Save Changes
          </ButtonPrimary>
        </div>
      </div>
    </div>
  );
}
